import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  BellIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import Sidebar from "./Sidebar";
import BottomNav from "./BottomNav";
import PageHeader from "./PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { logError } from "../utils/logger";

/**
 * Route-based header configuration
 * Maps each route to its corresponding header icon and title
 */
const ROUTE_HEADERS = {
  "/home": { icon: HomeIcon, title: "Nearby Notes" },
  "/notes": { icon: ClipboardDocumentListIcon, title: "Notes" },
  "/notifications": { icon: BellIcon, title: "Notifications" },
  "/messages": { icon: ChatBubbleOvalLeftEllipsisIcon, title: "Messages" },
  "/settings": { icon: Cog6ToothIcon, title: "Settings" },
};

/**
 * Dynamic route header for shared note pages
 */
const NOTE_PAGE_HEADER = {
  icon: DocumentTextIcon,
  title: "Note",
};

/**
 * AppLayout - Persistent layout wrapper for all protected routes
 *
 * This component wraps all authenticated pages and provides:
 * - A persistent Sidebar that doesn't remount on page changes
 * - A persistent PageHeader that updates based on current route
 * - Consistent layout structure across all pages
 *
 * The Outlet component renders the current route's content
 * while the Sidebar and PageHeader remain mounted across all navigations.
 */
export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Track active path based on current location
  const activePath = location.pathname;
  const isNotePage = activePath.startsWith("/n/");

  // Get header configuration for current route
  // Check for dynamic routes first (e.g., /n/:noteId)
  let headerConfig = ROUTE_HEADERS[activePath];

  // Handle shared note pages (/n/:noteId)
  if (!headerConfig && isNotePage) {
    headerConfig = NOTE_PAGE_HEADER;
  }

  // When on a note page that came from another page (e.g. notifications),
  // highlight that page's nav item instead of leaving nothing highlighted.
  const effectiveActivePath =
    isNotePage && location.state?.from ? location.state.from : activePath;

  const handleNavigation = (href) => {
    navigate(href);
  };

  // Back button for note pages: go back if we have history, else go home
  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  const handleLogout = async () => {
    try {
      await logout(); // Call logout from AuthContext (sets user to null)
      navigate("/"); // Then navigate to landing page
    } catch (err) {
      logError("Logout failed:", err);
    }
  };

  return (
    <div className="relative h-dvh bg-surface text-text-primary font-sans">
      {/* Main content area - full screen, behind sidebar */}
      <main className="absolute inset-0 flex flex-col overflow-hidden pb-16 md:pb-0">
        {/* Persistent PageHeader - content changes based on route */}
        {headerConfig && (
          <PageHeader
            icon={<headerConfig.icon className="h-5 w-5" />}
            title={headerConfig.title}
            onBack={isNotePage ? handleBack : undefined}
          />
        )}

        {/* Page content - renders the current route */}
        <Outlet />
      </main>

      {/* Persistent Sidebar - overlays main content, never unmounts during navigation */}
      <Sidebar
        activePath={effectiveActivePath}
        onNavigate={handleNavigation}
        onLogout={handleLogout}
      />

      {/* Mobile bottom navigation bar - visible only below md breakpoint */}
      <BottomNav
        activePath={effectiveActivePath}
        onNavigate={handleNavigation}
      />
    </div>
  );
}
